import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Edit, Trash2, Eye, Loader2, Sparkles, Send,
  FileText, Globe, Search, ImagePlus, ExternalLink, Copy,
  BarChart3, Calendar, CheckCircle, Settings
} from "lucide-react";

const ConsultantBlog = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [genTopic, setGenTopic] = useState("");
  const [genCategory, setGenCategory] = useState("business-strategy");
  const [form, setForm] = useState({
    title: "", content: "", excerpt: "", category: "business-strategy",
    featured_image: "", meta_description: "", seo_keywords: [], status: "draft"
  });
  const [newKeyword, setNewKeyword] = useState("");
  const [fetchingImage, setFetchingImage] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showCatManager, setShowCatManager] = useState(false);
  const [newCatName, setNewCatName] = useState("");

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/consultant/blog/posts`);
      if (res.ok) { const d = await res.json(); setPosts(d.posts || []); setStats(d.stats || {}); }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchPosts(); fetchCategories(); }, []);

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API}/blog/categories`);
      if (res.ok) { const d = await res.json(); setCategories(d.categories || []); }
    } catch (e) { console.error(e); }
  };

  const addCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      const res = await authFetch(`${API}/consultant/blog/categories`, { method: "POST", body: JSON.stringify({ name: newCatName.trim() }) });
      if (res.ok) { toast.success("Category added"); setNewCatName(""); fetchCategories(); }
      else { const e = await res.json().catch(() => ({})); toast.error(e.detail || "Failed"); }
    } catch { toast.error("Failed"); }
  };

  const deleteCategory = async (catId) => {
    if (!confirm("Delete this category?")) return;
    const res = await authFetch(`${API}/consultant/blog/categories/${catId}`, { method: "DELETE" });
    if (res.ok) { toast.success("Category deleted"); fetchCategories(); }
  };

  const resetForm = () => {
    setForm({ title: "", content: "", excerpt: "", category: "business-strategy", featured_image: "", meta_description: "", seo_keywords: [], status: "draft" });
    setEditingPost(null);
  };

  const handleSave = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const url = editingPost ? `${API}/consultant/blog/posts/${editingPost.post_id}` : `${API}/consultant/blog/posts`;
      const method = editingPost ? "PUT" : "POST";
      const res = await authFetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
      if (res.ok) {
        toast.success(editingPost ? "Post updated" : "Post created");
        setShowEditor(false);
        resetForm();
        fetchPosts();
      } else { toast.error("Failed to save"); }
    } catch (e) { toast.error("Error saving"); }
    setSaving(false);
  };

  const handleDelete = async (postId) => {
    if (!window.confirm("Delete this blog post?")) return;
    try {
      const res = await authFetch(`${API}/consultant/blog/posts/${postId}`, { method: "DELETE" });
      if (res.ok) { toast.success("Post deleted"); fetchPosts(); }
    } catch (e) { toast.error("Error deleting"); }
  };

  const handleGenerate = async () => {
    if (!genTopic) { toast.error("Enter a topic"); return; }
    setGenerating(true);
    toast.info("Generating blog post... this may take 15-30 seconds");
    try {
      const res = await authFetch(`${API}/consultant/blog/generate`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ topic: genTopic, category: genCategory })
      });
      if (res.ok) {
        const data = await res.json();
        setForm({
          title: data.title || genTopic,
          content: data.content || "",
          excerpt: data.excerpt || "",
          category: data.category || genCategory,
          featured_image: "",
          meta_description: data.meta_description || "",
          seo_keywords: data.seo_keywords || [],
          status: "draft"
        });
        setShowGenerate(false);
        setShowEditor(true);
        toast.success("Blog post generated! Review and publish.");
        if (data.suggested_image_query) {
          fetchStockImage(data.suggested_image_query);
        }
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Generation failed. Please try again.");
      }
    } catch (e) {
      console.error("Blog generation error:", e);
      toast.error("Generation timed out or failed. Please try again.");
    }
    setGenerating(false);
  };

  const fetchStockImage = async (query) => {
    setFetchingImage(true);
    try {
      // Use backend to search for relevant image
      const res = await authFetch(`${API}/consultant/blog/search-image?query=${encodeURIComponent(query)}`);
      if (res.ok) {
        const data = await res.json();
        if (data.image_url) {
          setForm(prev => ({ ...prev, featured_image: data.image_url }));
          setFetchingImage(false);
          return;
        }
      }
    } catch (e) { console.error("Image search error:", e); }
    setFetchingImage(false);
  };

  const openEdit = (post) => {
    setEditingPost(post);
    setForm({
      title: post.title || "", content: post.content || "", excerpt: post.excerpt || "",
      category: post.category || "business-strategy", featured_image: post.featured_image || "",
      meta_description: post.meta_description || "", seo_keywords: post.seo_keywords || [],
      status: post.status || "draft"
    });
    setShowEditor(true);
  };

  const addKeyword = () => {
    if (newKeyword.trim() && !form.seo_keywords.includes(newKeyword.trim())) {
      setForm({ ...form, seo_keywords: [...form.seo_keywords, newKeyword.trim()] });
      setNewKeyword("");
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <header className="bg-white border-b px-4 sm:px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/consultant")}><ArrowLeft className="w-4 h-4" /></Button>
            <div>
              <h1 className="text-lg font-bold">Blog Manager</h1>
              <p className="text-xs text-neutral-500">Create & manage blog posts</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowCatManager(true)} className="text-neutral-600 border-neutral-300 hover:bg-neutral-50" data-testid="manage-categories-btn">
              <Settings className="w-4 h-4 mr-1" /> Categories
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowGenerate(true)} className="text-purple-600 border-purple-200 hover:bg-purple-50">
              <Sparkles className="w-4 h-4 mr-1" /> AI Generate
            </Button>
            <Button size="sm" onClick={() => { resetForm(); setShowEditor(true); }} className="bg-neutral-900 text-white">
              <Plus className="w-4 h-4 mr-1" /> New Post
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          {[
            { label: "Total Posts", value: stats.total || 0, color: "text-neutral-900" },
            { label: "Published", value: stats.published || 0, color: "text-emerald-600" },
            { label: "Drafts", value: stats.draft || 0, color: "text-amber-600" },
            { label: "Total Views", value: stats.total_views || 0, color: "text-blue-600" }
          ].map((s, i) => (
            <Card key={i}><CardContent className="pt-4 pb-3 text-center">
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-xs text-neutral-500">{s.label}</p>
            </CardContent></Card>
          ))}
        </div>

        {/* Posts List */}
        {loading ? (
          <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : posts.length > 0 ? (
          <div className="space-y-3">
            {posts.map(post => (
              <Card key={post.post_id} className="hover:shadow-sm transition-shadow">
                <CardContent className="py-4 flex items-center gap-4">
                  {post.featured_image && <img src={post.featured_image} alt="" className="w-20 h-14 object-cover rounded border flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-sm truncate">{post.title}</h3>
                      <Badge className={post.status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"} variant="outline">
                        {post.status}
                      </Badge>
                    </div>
                    <p className="text-xs text-neutral-500 truncate">{post.excerpt}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-neutral-400">
                      <span>{post.created_at ? new Date(post.created_at).toLocaleDateString() : ""}</span>
                      <span>{post.views || 0} views</span>
                      <span className="capitalize">{post.category?.replace(/-/g, " ")}</span>
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    {post.status === "published" && (
                      <Button size="sm" variant="ghost" onClick={() => window.open(`/blog/${post.slug}`, "_blank")}><ExternalLink className="w-4 h-4" /></Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => openEdit(post)}><Edit className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(post.post_id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-dashed"><CardContent className="py-16 text-center">
            <FileText className="w-12 h-12 mx-auto mb-3 text-neutral-300" />
            <p className="text-neutral-500 mb-4">No blog posts yet</p>
            <Button onClick={() => setShowGenerate(true)} className="bg-purple-600 text-white"><Sparkles className="w-4 h-4 mr-2" />Generate Your First Post with AI</Button>
          </CardContent></Card>
        )}
      </main>

      {/* AI Generate Dialog */}
      <Dialog open={showGenerate} onOpenChange={setShowGenerate}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-purple-600" />AI Blog Generator</DialogTitle>
            <DialogDescription>Enter a topic and we'll create a full SEO-optimized blog post with a relevant photo.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div><Label>Topic *</Label><Input value={genTopic} onChange={e => setGenTopic(e.target.value)} placeholder="e.g., 5 Ways AI Can Improve Your Small Business Operations" /></div>
            <div>
              <Label>Category</Label>
              <Select value={genCategory} onValueChange={setGenCategory}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerate(false)}>Cancel</Button>
            <Button onClick={handleGenerate} disabled={generating} className="bg-purple-600 text-white">
              {generating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Generating...</> : <><Sparkles className="w-4 h-4 mr-2" />Generate Post</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={(o) => { if (!o) { setShowEditor(false); resetForm(); } }}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPost ? "Edit Blog Post" : "Create Blog Post"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Blog post title" /></div>
            <div><Label>Excerpt</Label><Input value={form.excerpt} onChange={e => setForm({...form, excerpt: e.target.value})} placeholder="Brief summary (160 chars)" maxLength={160} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Featured Image URL</Label>
              <div className="flex gap-2">
                <Input value={form.featured_image} onChange={e => setForm({...form, featured_image: e.target.value})} placeholder="https://..." className="flex-1" />
                <Button size="sm" variant="outline" onClick={() => fetchStockImage(form.title || "business")} disabled={fetchingImage}>
                  {fetchingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
                </Button>
              </div>
              {form.featured_image && <img src={form.featured_image} alt="Preview" className="mt-2 w-full max-h-40 object-cover rounded border" />}
            </div>
            <div><Label>Content (HTML)</Label><Textarea value={form.content} onChange={e => setForm({...form, content: e.target.value})} rows={12} placeholder="Blog content in HTML..." className="font-mono text-xs" /></div>
            <div><Label>Meta Description (SEO)</Label><Input value={form.meta_description} onChange={e => setForm({...form, meta_description: e.target.value})} placeholder="SEO meta description (155 chars)" maxLength={155} /></div>
            <div>
              <Label>SEO Keywords</Label>
              <div className="flex gap-2 mb-2">
                <Input value={newKeyword} onChange={e => setNewKeyword(e.target.value)} placeholder="Add keyword" onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addKeyword())} className="flex-1" />
                <Button size="sm" variant="outline" onClick={addKeyword}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-1">
                {form.seo_keywords.map((kw, i) => (
                  <Badge key={i} variant="outline" className="text-xs cursor-pointer hover:bg-red-50 hover:text-red-600" onClick={() => setForm({...form, seo_keywords: form.seo_keywords.filter((_, j) => j !== i)})}>
                    {kw} &times;
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setShowEditor(false); resetForm(); }}>Cancel</Button>
            <Button onClick={() => { setForm({...form, status: "draft"}); handleSave(); }} variant="outline" disabled={saving}>Save Draft</Button>
            <Button onClick={() => { setForm({...form, status: "published"}); setTimeout(handleSave, 0); }} disabled={saving} className="bg-emerald-600 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Globe className="w-4 h-4 mr-2" />}
              Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Category Manager Dialog */}
      <Dialog open={showCatManager} onOpenChange={setShowCatManager}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Manage Blog Categories</DialogTitle>
            <DialogDescription>Add or remove categories for blog posts</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex gap-2">
              <Input value={newCatName} onChange={e => setNewCatName(e.target.value)} placeholder="New category name" onKeyDown={e => e.key === "Enter" && addCategory()} data-testid="new-category-input" />
              <Button onClick={addCategory} disabled={!newCatName.trim()} className="bg-[#333333] text-white" data-testid="add-category-btn">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {categories.map(c => (
                <div key={c.id} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                  <div>
                    <p className="font-medium text-sm text-neutral-900">{c.name}</p>
                    <p className="text-xs text-neutral-500 font-mono">{c.id}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {c.count > 0 && <Badge variant="outline" className="text-xs">{c.count} posts</Badge>}
                    <Button size="sm" variant="ghost" className="text-red-500 hover:bg-red-50" onClick={() => deleteCategory(c.id)} data-testid={`delete-cat-${c.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
              {categories.length === 0 && <p className="text-neutral-500 text-center py-4 text-sm">No categories yet</p>}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConsultantBlog;
